import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Loader2, PlayCircle, Layers, Users, GraduationCap, Star, Search } from "lucide-react";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { money } from "@/lib/format";

const accessBadge = (c) => {
  if (c.access === "free") return { text: "Free", cls: "bg-[#5C8A72]/15 text-[#5C8A72]" };
  if (c.access === "membership") return { text: "Members", cls: "bg-[#144F75]/10 text-[#144F75]" };
  return { text: `${money((c.price || 0))}`, cls: "bg-[#D98E73]/15 text-[#B5794E]" };
};

export default function Courses() {
  const [cat, setCat] = useState("");
  const [q, setQ] = useState("");
  const { data, isLoading } = useQuery({ queryKey: ["courses"], queryFn: () => api.get("/courses").then((r) => r.data), refetchInterval: 60000 });
  const categories = [...new Set((data || []).map((c) => c.category).filter(Boolean))];
  const courses = (data || []).filter((c) =>
    (!cat || c.category === cat) &&
    (!q || `${c.title} ${c.subtitle} ${c.enhancer_name}`.toLowerCase().includes(q.toLowerCase())));

  return (
    <div data-testid="courses-catalog" className="min-h-[70vh]">
      <Seo
        title="Courses & Learning Programs"
        description="Online and in-person wellness courses, self-paced learning, and guided programs from experienced enhancers. Free, member, and purchase access available."
        path="/courses"
      />
      <section className="relative py-20 bg-[#144F75] overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(circle at 80% 20%, #fff 1px, transparent 1px)", backgroundSize: "26px 26px" }} />
        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <GraduationCap className="mx-auto text-[#D98E73] mb-4" size={38} />
          <p className="eyw-label !text-[#D98E73] mb-3">Learn with Enhancers</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-white leading-tight">Courses & Trainings</h1>
          <p className="text-white/80 text-lg mt-5 max-w-xl mx-auto">Self-paced courses from the Enhancers in our ecosystem — practices, teachings, and paths to enhance your world.</p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-14">
        {categories.length > 0 && (
          <div className="flex flex-wrap items-center gap-3 mb-8" data-testid="courses-filters">
            <div className="flex flex-wrap gap-2">
              {["", ...categories].map((c) => (
                <button key={c || "all"} onClick={() => setCat(c)} data-testid={`courses-cat-${(c || "all").toLowerCase().replace(/\s+/g, "-")}`}
                  className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${cat === c ? "bg-[#144F75] text-white border-[#144F75]" : "border-[#E5E0D8] text-[#4A5D6B] hover:border-[#144F75]/40 bg-white"}`}>
                  {c || "All"}
                </button>
              ))}
            </div>
            <div className="relative ml-auto">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8FA89B]" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search courses…" data-testid="courses-search" className="eyw-input !pl-9 w-56" />
            </div>
          </div>
        )}
        {isLoading ? (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#144F75]" size={32} /></div>
        ) : courses.length === 0 ? (
          <p className="text-center text-[#4A5D6B]">{(data || []).length === 0 ? "New programs are being prepared by our Enhancers. In the meantime, explore our offerings or upcoming events." : "No courses match your search."}</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {courses.map((c) => {
              const b = accessBadge(c);
              return (
                <Link key={c.id} to={`/courses/${c.slug || c.id}`} data-testid={`course-card-${c.id}`} className="eyw-card overflow-hidden group flex flex-col hover:-translate-y-1 transition-transform">
                  <div className="aspect-video bg-[#E5E0D8] relative overflow-hidden">
                    {c.thumbnail ? <img src={c.thumbnail} alt={c.title} className="w-full h-full object-cover" /> :
                      <div className="w-full h-full grid place-items-center bg-gradient-to-br from-[#144F75] to-[#0d3550]"><PlayCircle className="text-white/70" size={40} /></div>}
                    <span className={`absolute top-3 right-3 text-xs font-medium px-2.5 py-1 rounded-full ${b.cls} bg-white/95`}>{b.text}</span>
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <p className="eyw-label mb-1">{c.category}</p>
                    <h3 className="font-serif text-lg text-[#1A242B] group-hover:text-[#144F75] transition-colors">{c.title}</h3>
                    <p className="text-[#4A5D6B] text-sm mt-1 flex-1">{c.subtitle}</p>
                    {c.review_count > 0 && (
                      <div className="flex items-center gap-1 mt-2">
                        <Star size={13} className="fill-[#D98E73] text-[#D98E73]" />
                        <span className="text-xs text-[#4A5D6B]">{c.avg_rating.toFixed(1)} ({c.review_count})</span>
                      </div>
                    )}
                    <div className="flex items-center gap-4 text-xs text-[#8FA89B] mt-4">
                      <span className="flex items-center gap-1"><Layers size={13} /> {c.lesson_count} lessons</span>
                      <span className="flex items-center gap-1"><Users size={13} /> {c.enrolled_count} enrolled</span>
                    </div>
                    <p className="text-xs text-[#4A5D6B] mt-2">by {c.enhancer_name}</p>
                    <span data-testid={`course-cta-${c.id}`} className="mt-4 inline-flex items-center text-sm font-semibold text-[#144F75] group-hover:translate-x-0.5 transition-transform">{c.access === "free" ? "Enroll free →" : "View program & enroll →"}</span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
